import { AloftPrice } from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectImprovementEntityIds } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectADUCost, selectADUCosts } from './selectADUCost.ts';
import { selectAtticCost, selectAtticCosts } from './selectAtticCost.ts';
import { selectBarnCost, selectBarnCosts } from './selectBarnCost.ts';
import {
  selectBelowGradeFullBathCost,
  selectBelowGradeFullBathCosts,
} from './selectBelowGradeFullBathCost.ts';
import {
  selectBelowGradeHalfBathCost,
  selectBelowGradeHalfBathCosts,
} from './selectBelowGradeHalfBathCost.ts';
import { selectCarportCost, selectCarportCosts } from './selectCarportCost.ts';
import { selectDeckCost, selectDeckCosts } from './selectDeckCost.ts';
import { selectDetachedGarageCost, selectDetachedGarageCosts } from './selectDetachedGarageCost.ts';
import {
  selectFinishedBasementAreaCost,
  selectFinishedBasementAreaCosts,
} from './selectFinishedBasementAreaCost.ts';
import { selectFireplaceCost, selectFireplaceCosts } from './selectFireplaceCost.ts';
import { selectFullBathCost, selectFullBathCosts } from './selectFullBathCost.ts';
import { selectGarageCost, selectGarageCosts } from './selectGarageCost.ts';
import { selectHalfBathCost, selectHalfBathCosts } from './selectHalfBathCost.ts';
import { selectMainStructureCost, selectMainStructureCosts } from './selectMainStructureCost.ts';
import { selectManualCost, selectManualCosts } from './selectManualCost.ts';
import { selectPatioCost, selectPatioCosts } from './selectPatioCost.ts';
import { selectPoolCost, selectPoolCosts } from './selectPoolCost.ts';
import { selectPorchCost, selectPorchCosts } from './selectPorchCost.ts';
import { selectShedCost, selectShedCosts } from './selectShedCost.ts';
import { selectSpaCost, selectSpaCosts } from './selectSpaCost.ts';
import {
  selectTotalBasementAreaCost,
  selectTotalBasementAreaCosts,
} from './selectTotalBasementAreaCost.ts';
import { selectWorkshopCost, selectWorkshopCosts } from './selectWorkshopCost.ts';

export type CostNewByCategory = {
  gla: AloftPrice;
  garage: AloftPrice;
  detachedGarage: AloftPrice;
  carport: AloftPrice;
  basementTotal: AloftPrice;
  basementFinished: AloftPrice;
  attic: AloftPrice;
  fireplace: AloftPrice;
  fullBath: AloftPrice;
  halfBath: AloftPrice;
  belowGradeFullBath: AloftPrice;
  belowGradeHalfBath: AloftPrice;
  shed: AloftPrice;
  workshop: AloftPrice;
  barn: AloftPrice;
  pool: AloftPrice;
  spa: AloftPrice;
  adu: AloftPrice;
  porch: AloftPrice;
  patio: AloftPrice;
  deck: AloftPrice;
  manualCost: AloftPrice;
};

const calculateCostNewByCategory = (
  gla: ReturnType<typeof selectMainStructureCost> = AloftPrice.zero,
  garage: ReturnType<typeof selectGarageCost> = AloftPrice.zero,
  detachedGarage: ReturnType<typeof selectDetachedGarageCost> = AloftPrice.zero,
  carport: ReturnType<typeof selectCarportCost> = AloftPrice.zero,
  basementTotal: ReturnType<typeof selectTotalBasementAreaCost> = AloftPrice.zero,
  basementFinished: ReturnType<typeof selectFinishedBasementAreaCost> = AloftPrice.zero,
  attic: ReturnType<typeof selectAtticCost> = AloftPrice.zero,
  fireplace: ReturnType<typeof selectFireplaceCost> = AloftPrice.zero,
  fullBath: ReturnType<typeof selectFullBathCost> = AloftPrice.zero,
  halfBath: ReturnType<typeof selectHalfBathCost> = AloftPrice.zero,
  belowGradeFullBath: ReturnType<typeof selectBelowGradeFullBathCost> = AloftPrice.zero,
  belowGradeHalfBath: ReturnType<typeof selectBelowGradeHalfBathCost> = AloftPrice.zero,
  shed: ReturnType<typeof selectShedCost> = AloftPrice.zero,
  workshop: ReturnType<typeof selectWorkshopCost> = AloftPrice.zero,
  barn: ReturnType<typeof selectBarnCost> = AloftPrice.zero,
  pool: ReturnType<typeof selectPoolCost> = AloftPrice.zero,
  spa: ReturnType<typeof selectSpaCost> = AloftPrice.zero,
  adu: ReturnType<typeof selectADUCost> = AloftPrice.zero,
  porch: ReturnType<typeof selectPorchCost> = AloftPrice.zero,
  patio: ReturnType<typeof selectPatioCost> = AloftPrice.zero,
  deck: ReturnType<typeof selectDeckCost> = AloftPrice.zero,
  manualCost: ReturnType<typeof selectManualCost> = AloftPrice.zero,
): CostNewByCategory => ({
  gla,
  garage,
  detachedGarage,
  carport,
  basementTotal,
  basementFinished,
  attic,
  fireplace,
  fullBath,
  halfBath,
  belowGradeFullBath,
  belowGradeHalfBath,
  shed,
  workshop,
  barn,
  pool,
  spa,
  adu,
  porch,
  patio,
  deck,
  manualCost,
});

export const selectCostNewByCategory = createSelector(
  [
    selectMainStructureCost,
    selectGarageCost,
    selectDetachedGarageCost,
    selectCarportCost,
    selectTotalBasementAreaCost,
    selectFinishedBasementAreaCost,
    selectAtticCost,
    selectFireplaceCost,
    selectFullBathCost,
    selectHalfBathCost,
    selectBelowGradeFullBathCost,
    selectBelowGradeHalfBathCost,
    selectShedCost,
    selectWorkshopCost,
    selectBarnCost,
    selectPoolCost,
    selectSpaCost,
    selectADUCost,
    selectPorchCost,
    selectPatioCost,
    selectDeckCost,
    selectManualCost,
  ],
  calculateCostNewByCategory,
);

export const selectCostNewByCategories = createSelector(
  [
    selectMainStructureCosts,
    selectGarageCosts,
    selectDetachedGarageCosts,
    selectCarportCosts,
    selectTotalBasementAreaCosts,
    selectFinishedBasementAreaCosts,
    selectAtticCosts,
    selectFireplaceCosts,
    selectFullBathCosts,
    selectHalfBathCosts,
    selectBelowGradeFullBathCosts,
    selectBelowGradeHalfBathCosts,
    selectShedCosts,
    selectWorkshopCosts,
    selectBarnCosts,
    selectPoolCosts,
    selectSpaCosts,
    selectADUCosts,
    selectPorchCosts,
    selectPatioCosts,
    selectDeckCosts,
    selectManualCosts,
    selectImprovementEntityIds,
  ],
  (
    glas,
    garages,
    detachedGarages,
    carports,
    basementTotals,
    basementFinisheds,
    attics,
    fireplaces,
    fullBaths,
    halfBaths,
    belowGradeFullBaths,
    belowGradeHalfBaths,
    sheds,
    workshops,
    barns,
    pools,
    spas,
    adus,
    porches,
    patios,
    decks,
    manualCosts,
    improvementEntityIds,
  ) =>
    improvementEntityIds.reduce((acc: Record<string, CostNewByCategory>, propID) => {
      acc[propID] = calculateCostNewByCategory(
        glas[propID],
        garages[propID],
        detachedGarages[propID],
        carports[propID],
        basementTotals[propID],
        basementFinisheds[propID],
        attics[propID],
        fireplaces[propID],
        fullBaths[propID],
        halfBaths[propID],
        belowGradeFullBaths[propID],
        belowGradeHalfBaths[propID],
        sheds[propID],
        workshops[propID],
        barns[propID],
        pools[propID],
        spas[propID],
        adus[propID],
        porches[propID],
        patios[propID],
        decks[propID],
        manualCosts[propID],
      );
      return acc;
    }, {}),
);
